import { useQuery } from '@tanstack/react-query';
import OutreachService from '@/services/OutreachService';
import type { OutreachRow } from '@/services/OutreachService';
import { OUTREACH_KEYS } from './keys';

export function useCommunicationLog(
  userid: string | number | null | undefined,
  options: { refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: OUTREACH_KEYS.log(userid ?? ''),
    queryFn: () => OutreachService.getCommunicationLog(userid!),
    enabled: userid != null && userid !== '',
    refetchInterval: options.refetchInterval ?? false,
  });
}

// Keyed per patient and never with placeholderData: Engage would pair the last patient's watermark with the next thread.
export function usePatientChatLastSeen(
  userid: string | number | null | undefined,
  options: { refetchInterval?: number | false | (() => number | false) } = {},
) {
  return useQuery({
    queryKey: OUTREACH_KEYS.patientLastSeen(userid ?? ''),
    queryFn: () => OutreachService.getPatientChatLastSeen(userid),
    enabled: userid != null && userid !== '',
    refetchInterval: options.refetchInterval ?? false,
  });
}

export function useLastOutreachDates() {
  return useQuery({
    queryKey: OUTREACH_KEYS.lastDates(),
    queryFn: () => OutreachService.getLastOutreachDates(),
  });
}

// Roster-wide patient-inbound timestamps, for ordering Engage by latest human
// interaction. Deliberately NOT self-polling: the read is an unbounded whole-table
// fetch, so the caller drives refresh off its own idle-gated interval rather than a
// `refetchInterval`, which only pauses on blur and would keep pulling on a focused
// idle tab. Keyed by program so a switch can't serve the previous program's map.
export function useLastInteractionDates(
  programid: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: OUTREACH_KEYS.lastInteractionDates(programid),
    queryFn: () => OutreachService.getLastInteractionDates(),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}

export type { OutreachRow };
